import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { useTheme } from "../hooks/use-theme";
import Icon from "./Icon";
import Text from "./Text";
import { getShadow } from "../utils";
import { OutsidePressHandler } from "./OutsidePressHandler";
import { transparentOverlay } from "../atoms/transparent-overlay";
import { useSetAtom } from "jotai";
import { DropdownComponentType, DropdownConfigType } from "../contexts/DropdownManager";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    StyleSheet,
    useWindowDimensions,
    Pressable,
    View,
    ScrollView,
    LayoutChangeEvent,
    StyleProp,
    ViewStyle,
} from "react-native";
import { IconName } from "./Icon";
import { scheduleOnRN } from "react-native-worklets";
import Switch from "../ui_atoms/Switch";

const AnimatedOutside = Animated.createAnimatedComponent(OutsidePressHandler);

export type DropdownItemsType = {
    icon?: IconName;
    color?: string;
    label: string;
    onClick: () => void | Promise<void>;
    selected?: boolean;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    multiline?: boolean;
};

type DropdownProps = {
    position: NonNullable<DropdownConfigType["position"]>;
    onClose: () => void;
    onLayout?: (e: LayoutChangeEvent) => void;
} & Pick<DropdownConfigType, "keepOpen" | "onBlur" | "uniqueSelection" | "component">;

const SCREEN_HORIZONTAL_PADDING = 40;

// isVisible = [false, true]
const OPACITY_VALUES = [0, 1];
const SCALE_VALUES = [0.85, 1];

const timingConfig = {
    duration: 200,
};

export const ITEM_HEIGHT = 44;

export const Dropdown = ({
    position,
    onLayout,
    onClose,
    onBlur,
    component: Component,
}: DropdownProps) => {
    const styles = useStyles();
    const { width: windowWidth } = useWindowDimensions();
    const setTransparentOverlay = useSetAtom(transparentOverlay);

    const { dropdownLeft, dropdownRight, dropdownTop } = useMemo(() => {
        const { x, width, pressX, y } = position;

        const left = pressX || x;
        const right = windowWidth - x - width;

        return { dropdownLeft: left, dropdownRight: right, dropdownTop: y };
    }, [windowWidth, position]);

    const isVisible = position.visible || false;

    const opacityValue = useSharedValue(OPACITY_VALUES[+isVisible]);
    const scaleValue = useSharedValue(SCALE_VALUES[+isVisible]);

    const animatedStyles = useAnimatedStyle(() => ({
        opacity: opacityValue.value,
        transform: [{ scale: scaleValue.value }],
    }));

    useEffect(() => {
        setTransparentOverlay(true);
        return () => {
            setTransparentOverlay(false);
        };
    }, [setTransparentOverlay]);

    const fadeOut = useCallback(() => {
        onBlur?.();
        scaleValue.value = withTiming(SCALE_VALUES[0], timingConfig);
        opacityValue.value = withTiming(OPACITY_VALUES[0], timingConfig, () => {
            scheduleOnRN(onClose);
        });
    }, [onBlur, scaleValue, opacityValue, onClose]);

    useEffect(() => {
        if (position.visible) {
            scaleValue.value = withTiming(SCALE_VALUES[+isVisible], timingConfig);
            opacityValue.value = withTiming(OPACITY_VALUES[+isVisible], timingConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    return (
        <AnimatedOutside
            onLayout={onLayout}
            style={[
                styles.container,
                {
                    transformOrigin: `${position.dropdownPosition === "left" ? "right" : "left"} top`,
                    maxWidth: windowWidth - SCREEN_HORIZONTAL_PADDING,
                    maxHeight: position.maxHeight,
                    top: dropdownTop,
                },
                position.dropdownPosition === "left"
                    ? { right: dropdownRight }
                    : { left: dropdownLeft },
                animatedStyles,
            ]}
            onOutsidePress={fadeOut}
        >
            {Component ? <Component fadeOut={fadeOut} /> : null}
        </AnimatedOutside>
    );
};

type GET_SIMPLE_DROPDOWN_TYPE = {
    items: NonNullable<DropdownItemsType[]>;
    keepOpen?: boolean;
    uniqueSelection?: boolean;
};

type DropdownItemsListProps = {
    items: DropdownItemsType[];
    keepOpen: boolean;
    uniqueSelection?: boolean;
    hasSelectable: boolean;
    fadeOut?: () => void;
};

const DropdownItemsList = ({
    items: itemsProp,
    keepOpen,
    uniqueSelection,
    hasSelectable,
    fadeOut,
}: DropdownItemsListProps) => {
    const styles = useStyles();
    const { colors } = useTheme();
    // Local copy that the user can toggle. Reset to `itemsProp` whenever the
    // parent provides a new list — done via the "adjusting state on prop
    // change" pattern (https://react.dev/learn/you-might-not-need-an-effect)
    // instead of a sync effect.
    const [items, setItems] = useState(itemsProp);
    const [prevItemsProp, setPrevItemsProp] = useState(itemsProp);
    if (itemsProp !== prevItemsProp) {
        setPrevItemsProp(itemsProp);
        setItems(itemsProp);
    }

    const hasOnlyOneSelected = useMemo(() => items.filter((e) => e.selected).length === 1, [items]);

    return (
        <ScrollView>
            {items.map((item, index) => {
                // The last selected option cannot be turned off: dropping to an empty selection
                // would leave the screen on its "nothing here" state, which reads as an error. The
                // row is disabled so that rule is visible — including when the list holds a single
                // option, which the previous `items.length > 1` guard let through and left looking
                // active while every press was a no-op (BAS-2914).
                const disabled = !!(item.selected && hasOnlyOneSelected);

                const onPress = () => {
                    setItems((prev) =>
                        prev.map((e) =>
                            e.label === item.label ? { ...e, selected: !e.selected } : e,
                        ),
                    );
                    void item.onClick?.();
                    if (!keepOpen) {
                        fadeOut?.();
                    }
                };

                return (
                    <Pressable
                        key={typeof item.label === "string" ? item.label : `item-${index}`}
                        style={[styles.item, item.style, index === 0 && { borderTopWidth: 0 }]}
                        disabled={disabled}
                        onPress={onPress}
                        testID={item.testID}
                    >
                        {item.icon && (
                            <Icon
                                size={24}
                                name={item.icon}
                                color={item.color}
                                style={[styles.icon]}
                            />
                        )}
                        <View style={[styles.label]}>
                            {typeof item.label === "string" ? (
                                <Text
                                    numberOfLines={item.multiline ? undefined : 1}
                                    style={[
                                        styles.labelText,
                                        !!item.color && { color: item.color },
                                        // Greys the label alongside the switch, so a disabled row
                                        // reads as disabled and not merely unresponsive.
                                        disabled && { color: colors.backdrop },
                                    ]}
                                >
                                    {item.label}
                                </Text>
                            ) : (
                                item.label
                            )}
                        </View>
                        {hasSelectable &&
                            (uniqueSelection ? (
                                <Icon
                                    name={
                                        item.selected ? "circle-dot-enabled" : "circle-dot-disabled"
                                    }
                                    size={24}
                                    style={styles.selectableIcon}
                                    color={
                                        item.selected ? item.color || colors.message : colors.body
                                    }
                                />
                            ) : (
                                <Switch
                                    onChange={onPress}
                                    disabled={disabled}
                                    value={item.selected || false}
                                    size={38}
                                />
                            ))}
                    </Pressable>
                );
            })}
        </ScrollView>
    );
};

export const useItemsDropdown = ({
    items,
    keepOpen = false,
    uniqueSelection,
}: GET_SIMPLE_DROPDOWN_TYPE) => {
    const hasSelectable = items.some((e) => e.selected !== undefined);

    return useCallback<DropdownComponentType>(
        ({ fadeOut }) => (
            <DropdownItemsList
                items={items}
                keepOpen={keepOpen}
                uniqueSelection={uniqueSelection}
                hasSelectable={hasSelectable}
                fadeOut={fadeOut}
            />
        ),
        [hasSelectable, items, keepOpen, uniqueSelection],
    );
};

const useStyles = () => {
    const { roundness, fonts, gridSize, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    position: "absolute",
                    backgroundColor: "white",
                    borderRadius: roundness,
                    ...getShadow({ level: 8 }),
                },
                item: {
                    display: "flex",
                    flexDirection: "row",
                    paddingHorizontal: 10,
                    alignItems: "flex-start",
                    overflow: "hidden",
                    paddingVertical: gridSize / 3,
                    borderTopColor: colors.backdrop,
                    borderTopWidth: 1,
                },
                icon: {
                    width: 24,
                    marginRight: 12,
                    flexGrow: 0,
                },
                label: {
                    flexShrink: 1,
                    flexGrow: 1,
                    flex: 0,
                },
                labelText: {
                    ...fonts.bodyMedium,
                    marginRight: gridSize / 2,
                },
                selectableIcon: {
                    flexGrow: 0,
                    flexShrink: 0,
                    width: 24,
                },
            }),
        [roundness, gridSize, colors.backdrop, fonts.bodyMedium],
    );
};
